import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination"
import { Badge } from "@/components/ui/badge"
import { 
  Activity, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Download, 
  RefreshCw, 
  Calendar,
  User,
  Globe,
  Clock,
  Filter,
  TrendingUp,
  BarChart3,
  Eye
} from "lucide-react"
import Loader from "@/components/loader/loader"
import { useAdminAuth } from "@/context/Admin"

export default function ActivityLog() {
  const navigate = useNavigate()
  const { admin, getAuditLogs, getAuditStats, getAuditFilters, exportAuditLogsCSV } = useAdminAuth()
  const isSuperAdmin = admin?.role === "super_admin"
  
  const [logs, setLogs] = useState([])
  const [stats, setStats] = useState(null)
  const [filterOptions, setFilterOptions] = useState({ actions: [], resourceTypes: [], statuses: [], admins: [] })
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  })
  
  // Filters - use 'all' for select defaults to avoid Radix UI empty string issue
  const [filters, setFilters] = useState({
    action: "all",
    resourceType: "all",
    status: "all",
    adminId: "all",
    startDate: "",
    endDate: "",
    ipAddress: "",
    search: ""
  })

  const fetchLogs = useCallback(async (showLoader = true) => {
    if (!isSuperAdmin) return
    
    try {
      if (showLoader) setLoading(true)
      setError(null)
      
      // Convert 'all' values back to empty strings for API filtering
      const cleanFilters = Object.fromEntries(
        Object.entries(filters)
          .map(([k, v]) => [k, v === "all" ? "" : v])
          .filter(([_, v]) => v !== "")
      )
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...cleanFilters
      }
      
      const response = await getAuditLogs(params)
      
      if (response) {
        setLogs(response.data || [])
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }))
      }
    } catch (err) {
      const message = err?.message || "Failed to fetch activity logs"
      setError(message)
      console.error("Error fetching logs:", err)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [isSuperAdmin, pagination.page, pagination.limit, filters, getAuditLogs])

  const fetchStats = useCallback(async () => {
    if (!isSuperAdmin) return
    
    try {
      const params = {}
      if (filters.startDate) params.startDate = filters.startDate
      if (filters.endDate) params.endDate = filters.endDate
      
      const response = await getAuditStats(params)
      if (response) {
        setStats(response.stats)
      }
    } catch (err) {
      console.error("Error fetching stats:", err)
    }
  }, [isSuperAdmin, filters.startDate, filters.endDate, getAuditStats])

  const fetchFilterOptions = useCallback(async () => {
    if (!isSuperAdmin) return
    
    try {
      const response = await getAuditFilters()
      if (response?.filters) {
        setFilterOptions(response.filters)
      }
    } catch (err) {
      console.error("Error fetching filter options:", err)
    }
  }, [isSuperAdmin, getAuditFilters])

  useEffect(() => {
    if (isSuperAdmin) {
      fetchLogs()
      fetchStats()
      fetchFilterOptions()
    }
  }, [isSuperAdmin, fetchLogs, fetchStats, fetchFilterOptions])

  const handleRefresh = async () => {
    setRefreshing(true)
    await Promise.all([fetchLogs(false), fetchStats()])
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      // Convert 'all' values back to empty strings for API filtering
      const params = Object.fromEntries(
        Object.entries(filters)
          .map(([k, v]) => [k, v === "all" ? "" : v])
          .filter(([_, v]) => v !== "")
      )
      await exportAuditLogsCSV(params)
    } catch (err) {
      console.error("Export failed:", err)
    } finally {
      setExporting(false)
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handlePageChange = (direction) => {
    if (direction === "next" && pagination.page < pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: prev.page + 1 }))
    } else if (direction === "prev" && pagination.page > 1) {
      setPagination(prev => ({ ...prev, page: prev.page - 1 }))
    }
  }

  const clearFilters = () => {
    setFilters({
      action: "all",
      resourceType: "all",
      status: "all",
      adminId: "all",
      startDate: "",
      endDate: "",
      ipAddress: "",
      search: ""
    })
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const viewLogDetails = (logId) => {
    navigate(`/admin/activity-log/${logId}`)
  }

  const getStatusBadge = (status) => {
    if (status === "success") {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <CheckCircle className="w-3 h-3 mr-1" />
          Success
        </Badge>
      )
    }
    return (
      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
        <XCircle className="w-3 h-3 mr-1" />
        Failed
      </Badge>
    )
  }

  const getActionBadge = (action) => {
    const actionColors = {
      login: "bg-blue-50 text-blue-700 border-blue-200",
      logout: "bg-gray-50 text-gray-700 border-gray-200",
      create_admin: "bg-green-50 text-green-700 border-green-200",
      delete_admin: "bg-red-50 text-red-700 border-red-200",
      update_admin: "bg-yellow-50 text-yellow-700 border-yellow-200",
      password_change: "bg-purple-50 text-purple-700 border-purple-200",
      password_reset: "bg-orange-50 text-orange-700 border-orange-200",
      revoke_session: "bg-pink-50 text-pink-700 border-pink-200",
      block_ip: "bg-red-50 text-red-700 border-red-200",
      unblock_ip: "bg-green-50 text-green-700 border-green-200",
    }

    const colorClass = actionColors[action] || "bg-gray-50 text-gray-700 border-gray-200"
    const displayAction = action?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Unknown"

    return (
      <Badge variant="outline" className={colorClass}>
        {displayAction}
      </Badge>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatRelativeTime = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return "Just now"
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return formatDate(dateString)
  }

  // Restrict access to super_admin only
  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 px-2 sm:px-4 lg:px-8">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-600" />
          <h1 className="text-2xl sm:text-3xl font-bold text-red-600 mb-2">
            Access Denied
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            You do not have permission to access this page. Only super administrators can view the activity log.
          </p>
        </div>
      </div>
    )
  }

  if (loading && logs.length === 0) {
    return <Loader />
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Activity className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary">Activity Log</h1>
            <p className="text-gray-600 mt-1">Track all admin actions across the platform.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button 
            variant="default" 
            size="sm" 
            onClick={handleExport}
            disabled={exporting}
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Actions</p>
                  <p className="text-2xl font-bold">{stats.total?.toLocaleString() || 0}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-primary opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Successful</p>
                  <p className="text-2xl font-bold text-green-600">{stats.success?.toLocaleString() || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{stats.failure?.toLocaleString() || 0}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                  <p className="text-2xl font-bold text-primary">{stats.successRate || 0}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-primary opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Action Filter */}
            <Select value={filters.action} onValueChange={(v) => handleFilterChange("action", v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {filterOptions.actions?.map(action => (
                  <SelectItem key={action} value={action}>
                    {action.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={filters.status} onValueChange={(v) => handleFilterChange("status", v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failure">Failed</SelectItem>
              </SelectContent>
            </Select>

            {/* Admin Filter */}
            <Select value={filters.adminId} onValueChange={(v) => handleFilterChange("adminId", v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Admins" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Admins</SelectItem>
                {filterOptions.admins?.map(adminItem => (
                  <SelectItem key={adminItem.id} value={adminItem.id.toString()}>
                    {adminItem.name} ({adminItem.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* IP Address */}
            <Input
              placeholder="Filter by IP..."
              value={filters.ipAddress}
              onChange={(e) => handleFilterChange("ipAddress", e.target.value)}
            />

            {/* Start Date */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="date"
                placeholder="Start Date"
                className="pl-9"
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
              />
            </div>

            {/* End Date */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="date"
                placeholder="End Date"
                className="pl-9"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </div>

            {/* Clear Filters */}
            <Button variant="outline" onClick={clearFilters} className="lg:col-span-2">
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Activity Log Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-primary">Activity Log</CardTitle>
              <CardDescription>
                Total: {pagination.total} actions
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <AlertCircle className="w-16 h-16 mb-4" />
              <p className="text-lg">{error}</p>
              <Button onClick={() => fetchLogs()} className="mt-4">
                Retry
              </Button>
            </div>
          ) : logs.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Admin</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Resource</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead className="text-right">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <div className="font-medium text-sm">{log.admin?.name || "Unknown"}</div>
                              <div className="text-xs text-muted-foreground">{log.admin?.email || "N/A"}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getActionBadge(log.action)}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <span className="text-muted-foreground">{log.resource_type || "-"}</span>
                            {log.resource_id && (
                              <span className="text-xs ml-1 text-gray-400">#{log.resource_id}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Globe className="w-3 h-3 text-muted-foreground" />
                            <span className="font-mono text-sm">{log.ip_address || "N/A"}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3 h-3 text-muted-foreground" />
                            <span className="text-sm" title={formatDate(log.created_at)}>
                              {formatRelativeTime(log.created_at)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewLogDetails(log.id)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-600">
                    Page {pagination.page} of {pagination.totalPages}
                  </div>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            handlePageChange("prev")
                          }}
                          className={pagination.page <= 1 ? "pointer-events-none opacity-50" : ""}
                        />
                      </PaginationItem>
                      <PaginationItem>
                        <span className="px-4 py-2 text-sm">
                          {pagination.page} / {pagination.totalPages}
                        </span>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            handlePageChange("next")
                          }}
                          className={pagination.page >= pagination.totalPages ? "pointer-events-none opacity-50" : ""}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Activity className="w-16 h-16 mx-auto mb-4" />
              <p className="text-lg">No activity logs found.</p>
              <p className="text-sm mt-2">Try adjusting your filters or check back later.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
